/**
 * Schmale Hülle um fetch. Hält das Sitzungstoken und setzt den
 * Authorization-Header.
 *
 * Das Sitzungstoken darf in sessionStorage liegen: es berechtigt nur zum
 * Abruf verschlüsselter Daten. Der private Schlüssel liegt bewusst NICHT
 * dort, sondern ausschließlich im Arbeitsspeicher (crypto/vault.ts).
 */

const TOKEN_KEY = 'kf.doc.session';

export const API_BASE = import.meta.env.VITE_API_BASE ?? '';

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) sessionStorage.setItem(TOKEN_KEY, token);
  else sessionStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(
    override readonly message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  responseType: 'json' | 'bytes' = 'json',
): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.authorization = `Bearer ${token}`;

  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers['content-type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE}${path}`, { method, headers, body: payload });

  if (!response.ok) {
    let message = `Fehler ${response.status}`;
    try {
      const parsed = (await response.json()) as { error?: string };
      if (parsed.error) message = parsed.error;
    } catch {
      // Antwort war kein JSON – die Statusmeldung genügt.
    }
    if (response.status === 401) setToken(null);
    throw new ApiError(message, response.status);
  }

  if (responseType === 'bytes') {
    return new Uint8Array(await response.arrayBuffer()) as T;
  }
  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  getBytes: (path: string) => request<Uint8Array>('GET', path, undefined, 'bytes'),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};
